import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  BsDatepickerConfig,
  BsLocaleService,
  DatepickerDateTooltipText
} from 'ngx-bootstrap/datepicker';
import { CustomControlValueAccessor } from '../../../../domains/shared/utils/custom-control-value-accessor/custom-control-value-accessor';
import {
  isPeriodicidadeAnual,
  isPeriodicidadeDiaria,
  isPeriodicidadeMensal,
  isPeriodicidadePeriodo,
  isPeriodicidadeSemestral,
  isPeriodicidadeTrimestral,
  Periodicidade,
  periodicidadeAnual,
  periodicidadeDiaria,
  periodicidadeMensal,
  periodicidadePeriodo,
  periodicidadeSemestral,
  periodicidadeTrimestral
} from '../../../../shared/periodicidade';
import {
  INovoDatepickerSettings,
  INovoDatepickerValue
} from './novo-datepicker-settings.interface';
import {
  buildPeriodicidadesList,
  getDateFromSemestre,
  getDateFromTrimestre,
  getSemestreFromDate,
  getTrimestreFromDate,
  MONTHS,
  parseLocalDate,
  toLocalDateString
} from './novo-datepicker.utils';
import {
  monthMatches,
  quarterMatches,
  semesterMatches,
  yearMatches
} from './utils/selection.utils';
import {
  isMonthAfterMax,
  isQuarterAfterMax,
  isSemesterAfterMax,
  isYearAfterMax
} from './utils/limits.utils';
import { computeStep, getStepTitle } from './utils/step.utils';
import {
  computeYearsRange,
  defaultYearsPageStart,
  YEARS_PAGE_SIZE
} from './utils/years.utils';

const ALL_PERIODICIDADES: Periodicidade[] = [
  periodicidadeDiaria,
  periodicidadeMensal,
  periodicidadeTrimestral,
  periodicidadeSemestral,
  periodicidadeAnual,
  periodicidadePeriodo
];

const PANEL_WIDTH = 310;
const PANEL_HEIGHT_ESTIMATE = 320;

@Component({
  selector: 'sgt-novo-datepicker',
  templateUrl: './novo-datepicker.component.html',
  styleUrls: ['./novo-datepicker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NovoDatepickerComponent),
      multi: true
    }
  ]
})
export class NovoDatepickerComponent
  extends CustomControlValueAccessor<INovoDatepickerValue | null>
  implements OnInit, OnChanges, OnDestroy {
  @Input() emptyText = 'Selecione a Data';
  @Input() settings: Partial<INovoDatepickerSettings> = {};
  @Input() periodicidade: number = periodicidadeDiaria.id;
  @Input() withNavigation = false;
  @Input() minDate: Date = null;
  @Input() maxDate: Date = null;
  @Input() disableDates: Date[] = [];
  @Input() dateTooltipTexts: DatepickerDateTooltipText[] = [];
  @Output() periodicidadeChange = new EventEmitter<number>();
  @Output() dateChange = new EventEmitter<INovoDatepickerValue | null>();

  showPicker = false;
  panelAlignRight = false;
  panelStyle: { [key: string]: string } = {};
  inputText = '';
  periodicidades: Periodicidade[] = [];

  currentYear: number = new Date().getFullYear();
  yearsRange: number[] = [];
  private yearsPageStart: number;

  bsDateValue: Date;
  bsDateRangeValue: Date[];

  bsConfig: Partial<BsDatepickerConfig> = {
    containerClass: 'theme-periodo',
    showWeekNumbers: false,
    minMode: 'day',
    startView: 'day'
  };

  readonly months = MONTHS;
  readonly quarters = [1, 2, 3, 4];
  readonly semesters = [1, 2];

  /**
   * Mapa de funções de formatação de data indexadas pelo ID da periodicidade.
   * Cada função recebe uma Date e retorna a string formatada para exibição no input.
   * - Diária: dd/MM/yyyy
   * - Mensal: "Mês Ano" (capitalizado, em pt-BR)
   * - Trimestral: "T{n} {ano}"
   * - Semestral: "S{n} {ano}"
   * - Anual: "{ano}"
   * - Período: vazio (o texto é montado em buildInputText com as duas datas)
   */
  private readonly dateFormatters: Record<number, (d: Date) => string> = {
    [periodicidadeDiaria.id]: (d) =>
      this.datePipe.transform(d, 'dd/MM/yyyy') ?? '',
    [periodicidadeMensal.id]: (d) =>
      (
        this.datePipe.transform(d, 'MMMM yyyy', undefined, 'pt-BR') ?? ''
      ).replace(/^\w/, (c) => c.toUpperCase()),
    [periodicidadeTrimestral.id]: (d) =>
      `T${getTrimestreFromDate(d)} ${d.getFullYear()}`,
    [periodicidadeSemestral.id]: (d) =>
      `S${getSemestreFromDate(d)} ${d.getFullYear()}`,
    [periodicidadeAnual.id]: (d) => `${d.getFullYear()}`,
    [periodicidadePeriodo.id]: () => ''
  };

  constructor(
    private datePipe: DatePipe,
    private elementRef: ElementRef,
    private localeService: BsLocaleService
  ) {
    super();
    this.localeService.use('pt-br');
  }

  ngOnInit() {
    this.applySettings();
    this.buildYearsRange();
    document.addEventListener('scroll', this.onDocumentScroll, {
      capture: true
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['settings'] && !changes['settings'].isFirstChange()) {
      this.applySettings();
    }
    if (changes['periodicidade'] && !changes['periodicidade'].isFirstChange()) {
      const ids = this.periodicidades.map((p) => p.id);
      if (ids.length > 0 && !ids.includes(this.periodicidade)) {
        this.periodicidade = this.periodicidades[0].id;
        this.periodicidadeChange.emit(this.periodicidade);
      }
      this.buildYearsRange();
    }
  }

  private readonly onDocumentScroll = (event: Event) => {
    if (
      this.showPicker &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.showPicker = false;
    }
  };

  get isDiaria() {
    return isPeriodicidadeDiaria(this.periodicidade);
  }

  get isMensal() {
    return isPeriodicidadeMensal(this.periodicidade);
  }

  get isTrimestral() {
    return isPeriodicidadeTrimestral(this.periodicidade);
  }

  get isSemestral() {
    return isPeriodicidadeSemestral(this.periodicidade);
  }

  get isAnual() {
    return isPeriodicidadeAnual(this.periodicidade);
  }

  get isPeriodo() {
    return isPeriodicidadePeriodo(this.periodicidade);
  }

  private applySettings() {
    this.setDisabledPorSettings(!!this.settings.disabled);

    const configured = buildPeriodicidadesList(this.settings.periodicidades);
    this.periodicidades =
      configured.length > 0 ? configured : [...ALL_PERIODICIDADES];

    const ids = this.periodicidades.map((p) => p.id);
    if (!ids.includes(this.periodicidade)) {
      this.periodicidade = this.periodicidades[0].id;
    }

    const daysDisabled: number[] = [];
    if (this.settings.disableSunday) {
      daysDisabled.push(0);
    }
    if (this.settings.disableSaturday) {
      daysDisabled.push(6);
    }
    this.bsConfig = {
      ...this.bsConfig,
      daysDisabled,
      minDate: this.minDate ?? undefined,
      maxDate: this.maxDate ?? undefined
    };
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showPicker = false;
    }
  }

  protected onDisabledStateChange(isDisabled: boolean): void {
    if (isDisabled) {
      this.showPicker = false;
    }
  }

  openPicker() {
    if (this.disabled) {
      return;
    }
    this.showPicker = !this.showPicker;
    if (this.showPicker) {
      this.updatePanelPosition();
    }
  }

  private updatePanelPosition() {
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    this.panelAlignRight = rect.left + PANEL_WIDTH > window.innerWidth;
    const openAbove = window.innerHeight - rect.bottom < PANEL_HEIGHT_ESTIMATE;
    const horizontal = this.panelAlignRight
      ? { right: `${window.innerWidth - rect.right}px`, left: 'auto' }
      : { left: `${rect.left}px`, right: 'auto' };
    const vertical = openAbove
      ? { bottom: `${window.innerHeight - rect.top + 4}px`, top: 'auto' }
      : { top: `${rect.bottom + 4}px`, bottom: 'auto' };
    this.panelStyle = { ...horizontal, ...vertical };
  }

  selectPeriodicidade(p: Periodicidade) {
    this.periodicidade = p.id;
    this.periodicidadeChange.emit(this.periodicidade);
    this.buildYearsRange();

    if (this._value) {
      this.descartarValor();
    }

    this.bsDateValue = this.singleValue;
    this.bsDateRangeValue = this.rangeValue;
  }

  /**
   * Descarta a data ao trocar de periodicidade: o valor é indivisível da sua
   * granularidade ("Agosto 2026" não é "agosto" mais uma configuração), então
   * converter 15/08 em "agosto" ampliaria o filtro sem o usuário pedir. Um
   * campo vazio é visivelmente vazio; um campo convertido parece intencional.
   *
   * O painel permanece aberto e `dateChange` não dispara: trocar a
   * granularidade não é escolher uma data — isso continua sendo papel do
   * clique na grade.
   */
  private descartarValor() {
    this._value = null;
    this.bsDateValue = null;
    this.bsDateRangeValue = null;
    this.inputText = '';
    this.onChange(null);
  }

  selectToday() {
    this.onDaySelected(new Date());
  }

  prev(): void {
    this.step(-1);
  }

  next(): void {
    this.step(+1);
  }

  get prevTitle(): string {
    return this.stepTitle(-1);
  }

  get nextTitle(): string {
    return this.stepTitle(+1);
  }

  private stepTitle(direction: 1 | -1): string {
    if (this.isPeriodo) {
      return 'Navegação indisponível para período';
    }
    const periodicidade = this.currentPeriodicidade();
    return this.canNavigate() && periodicidade
      ? getStepTitle(periodicidade, direction)
      : '';
  }

  private currentPeriodicidade(): Periodicidade | undefined {
    return this.periodicidades.find((p) => p.id === this.periodicidade);
  }

  get singleValueExists(): boolean {
    return this.singleValue !== null;
  }

  private canNavigate(): boolean {
    return this.withNavigation && !this.isPeriodo && this.singleValueExists;
  }

  /** As setas respeitam o mesmo limite das grades: sem isto elas driblariam a trava. */
  private isForaDosLimites(date: Date): boolean {
    return (
      (this.maxDate != null && date > this.maxDate) ||
      (this.minDate != null && date < this.minDate)
    );
  }

  get canStepForward(): boolean {
    return this.canStep(+1);
  }

  get canStepBackward(): boolean {
    return this.canStep(-1);
  }

  private canStep(direction: 1 | -1): boolean {
    const current = this.singleValue;
    const periodicidade = this.currentPeriodicidade();
    if (!current || !periodicidade) {
      return false;
    }
    const newDate = computeStep(current, periodicidade, direction);
    return !!newDate && !this.isForaDosLimites(newDate);
  }

  private step(direction: 1 | -1): void {
    if (this.disabled || !this.canNavigate()) {
      return;
    }
    const current = this.singleValue;
    const periodicidade = this.currentPeriodicidade();
    if (!current || !periodicidade) {
      return;
    }
    const newDate = computeStep(current, periodicidade, direction);
    if (!newDate || this.isForaDosLimites(newDate)) {
      return;
    }
    this.emitSelection(newDate);
    this.syncFromValue(toLocalDateString(newDate));
  }

  onDaySelected(date: Date) {
    if (!date) {
      return;
    }
    const current = this.singleValue;
    if (current && toLocalDateString(date) === toLocalDateString(current)) {
      return;
    }
    this.bsDateValue = date;
    setTimeout(() => {
      this.emitSelection(date);
      this.showPicker = false;
    });
  }

  prevYear() {
    this.currentYear--;
  }

  nextYear() {
    this.currentYear++;
  }

  trackByYear(_index: number, year: number): number {
    return year;
  }

  isDisabledMonth(month: number): boolean {
    return isMonthAfterMax(month, this.currentYear, this.maxDate);
  }

  isDisabledQuarter(quarter: number, year: number): boolean {
    return isQuarterAfterMax(quarter, year, this.maxDate);
  }

  isDisabledSemester(semester: number, year: number): boolean {
    return isSemesterAfterMax(semester, year, this.maxDate);
  }

  isDisabledYear(year: number): boolean {
    return isYearAfterMax(year, this.maxDate);
  }

  isSelectedMonth(month: number): boolean {
    return monthMatches(this.singleValue, month, this.currentYear);
  }

  onMonthSelected(month: number) {
    const date = new Date(this.currentYear, month - 1, 1);
    this.emitSelection(date);
    this.showPicker = false;
  }

  isSelectedQuarter(q: number, year: number): boolean {
    return quarterMatches(this.singleValue, q, year);
  }

  onQuarterSelected(q: number, year: number) {
    const date = getDateFromTrimestre(q, year);
    this.emitSelection(date);
    this.showPicker = false;
  }

  isSelectedSemester(s: number, year: number): boolean {
    return semesterMatches(this.singleValue, s, year);
  }

  onSemesterSelected(s: number, year: number) {
    const date = getDateFromSemestre(s, year);
    this.emitSelection(date);
    this.showPicker = false;
  }

  isSelectedYear(year: number): boolean {
    return yearMatches(this.singleValue, year);
  }

  prevYearsPage() {
    this.yearsPageStart -= YEARS_PAGE_SIZE;
    this.buildYearsRange();
  }

  nextYearsPage() {
    this.yearsPageStart += YEARS_PAGE_SIZE;
    this.buildYearsRange();
  }

  onYearSelected(year: number) {
    const date = new Date(year, 0, 1);
    this.emitSelection(date);
    this.showPicker = false;
  }

  onPeriodSelected(dates: Date[]) {
    if (!dates?.[0] || !dates?.[1]) {
      return;
    }
    const current = this.rangeValue;
    if (
      current &&
      toLocalDateString(current[0]) === toLocalDateString(dates[0]) &&
      toLocalDateString(current[1]) === toLocalDateString(dates[1])
    ) {
      return;
    }
    this.bsDateRangeValue = dates;
    this.emitSelection(dates);
    this.showPicker = false;
  }

  clear() {
    this._value = null;
    this.bsDateValue = null;
    this.bsDateRangeValue = null;
    this.inputText = '';
    this.showPicker = false;
    this.onChange(null);
  }

  protected updateValue(value: INovoDatepickerValue | null): void {
    if (value?.periodicidade != null) {
      this.periodicidade = value.periodicidade;
      this.buildYearsRange();
    }
    this.syncFromValue(value?.date ?? null);
    this.buildInputText(value?.date ?? null);
  }

  private syncFromValue(value: string | string[] | null) {
    if (!value) {
      this.bsDateValue = null;
      this.bsDateRangeValue = null;
      return;
    }
    if (Array.isArray(value)) {
      this.bsDateRangeValue = value.map((v) => parseLocalDate(v));
    } else {
      const d = parseLocalDate(value);
      this.bsDateValue = d;
      this.currentYear = d.getFullYear();
      const newPageStart = defaultYearsPageStart(d.getFullYear());
      if (newPageStart !== this.yearsPageStart) {
        this.yearsPageStart = newPageStart;
        this.buildYearsRange();
      }
    }
  }

  private buildYearsRange() {
    const periodicidade = this.currentPeriodicidade();
    if (!periodicidade) {
      return;
    }
    const result = computeYearsRange(periodicidade, this.yearsPageStart);
    this.yearsRange = result.years;
    this.yearsPageStart = result.pageStart;
  }

  private buildInputText(value: string | string[] | null) {
    if (!value) {
      this.inputText = '';
      return;
    }

    if (Array.isArray(value)) {
      const [start, end] = value.map((v) => parseLocalDate(v));
      const s = this.datePipe.transform(start, 'dd/MM/yyyy') ?? '';
      const e = end ? this.datePipe.transform(end, 'dd/MM/yyyy') : '...';
      this.inputText = `${s} — ${e}`;
      return;
    }

    const date = parseLocalDate(value);
    const formatter = this.dateFormatters[this.periodicidade];
    this.inputText = formatter ? formatter(date) : '';
  }

  private emitSelection(value: Date | Date[]) {
    const isoDate = Array.isArray(value)
      ? value.map((d) => toLocalDateString(d))
      : toLocalDateString(value);

    const compoundValue: INovoDatepickerValue = {
      date: isoDate,
      periodicidade: this.periodicidade
    };

    this._value = compoundValue;
    this.buildInputText(isoDate);
    this.onChange(compoundValue);
    this.dateChange.emit(compoundValue);
    this.markAsTouched();
  }

  private get singleValue(): Date | null {
    const v = this._value;
    if (!v) {
      return null;
    }
    const date = v.date;
    if (!date || Array.isArray(date)) {
      return null;
    }
    return parseLocalDate(date);
  }

  private get rangeValue(): Date[] | null {
    const v = this._value;
    if (!v) {
      return null;
    }
    const date = v.date;
    if (!Array.isArray(date)) {
      return null;
    }
    return date.map((d) => parseLocalDate(d));
  }

  ngOnDestroy() {
    document.removeEventListener('scroll', this.onDocumentScroll, {
      capture: true
    });
  }
}
